import { format, parseISO } from 'date-fns';

/**
 * Utility functions for creating consistent history/audit log entries
 * across all Location Admin sections
 */

/**
 * Format a field value for display in history
 * @param {any} value - The value to format
 * @param {string} fieldType - Type of field (optional)
 * @returns {string} Formatted display value
 */
export const formatFieldValue = (value, fieldType = null) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  // Handle dates
  if (fieldType === 'date' && value) {
    try {
      const date = typeof value === 'string' ? parseISO(value) : value;
      return format(date, 'MMM d, yyyy');
    } catch {
      return value;
    }
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle objects (like address)
  if (typeof value === 'object' && value !== null) {
    if (value.street_1 !== undefined) {
      // Address object
      const parts = [
        value.street_1,
        value.street_2,
        value.city,
        value.state,
        value.zipCode
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '-';
    }
    // Other objects - stringify
    return JSON.stringify(value);
  }

  return String(value);
};

/**
 * Compare two values to determine if they've changed
 * @param {any} oldValue - Original value
 * @param {any} newValue - New value
 * @returns {boolean} True if values are different
 */
export const hasFieldChanged = (oldValue, newValue) => {
  // Handle null/undefined cases
  if (oldValue === null || oldValue === undefined || oldValue === '') {
    oldValue = '';
  }
  if (newValue === null || newValue === undefined || newValue === '') {
    newValue = '';
  }

  // Deep comparison for objects
  if (typeof oldValue === 'object' && typeof newValue === 'object') {
    return JSON.stringify(oldValue) !== JSON.stringify(newValue);
  }

  return oldValue !== newValue;
};

/**
 * Create a field change entry for audit log
 * @param {string} fieldName - Name of the field
 * @param {any} oldValue - Original value
 * @param {any} newValue - New value
 * @param {string} fieldType - Type of field (optional)
 * @returns {object} Field change object for audit log
 */
export const createFieldChange = (fieldName, oldValue, newValue, fieldType = null) => {
  return {
    fieldName: fieldName,
    oldValue: oldValue || null,
    oldDisplayValue: formatFieldValue(oldValue, fieldType),
    newValue: newValue || null,
    newDisplayValue: formatFieldValue(newValue, fieldType)
  };
};

/**
 * Build field changes array for multiple fields
 * @param {object} currentData - Current/original data object
 * @param {object} updatedData - Updated data object
 * @param {object} fieldMappings - Mapping of field names to display names and types
 * @returns {array} Array of field change objects
 */
export const buildFieldChanges = (currentData, updatedData, fieldMappings) => {
  const fieldChanges = [];

  Object.keys(fieldMappings).forEach(fieldKey => {
    const mapping = fieldMappings[fieldKey];
    const currentValue = getNestedValue(currentData, fieldKey);
    const updatedValue = getNestedValue(updatedData, fieldKey);

    if (hasFieldChanged(currentValue, updatedValue)) {
      fieldChanges.push(createFieldChange(
        mapping.displayName || fieldKey,
        currentValue,
        updatedValue,
        mapping.type
      ));
    }
  });

  return fieldChanges;
};

/**
 * Get nested object value using dot notation
 * @param {object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'address.street_1')
 * @returns {any} Value at path or undefined
 */
const getNestedValue = (obj, path) => {
  if (!obj) return undefined;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return undefined;
    }
  }
  
  return value;
};

/**
 * Create standard audit log data object
 * @param {object} params - Parameters for audit log
 * @returns {object} Formatted audit log data
 */
export const createAuditLogData = ({
  clientId,
  changeType,
  entityType,
  entityId,
  entityName,
  section,
  fieldChanges,
  changeReason,
  userId,
  userName,
  affectedLocations,
  affectedOrgUnits
}) => {
  // Build change summary based on section and changes
  let changeSummary = '';
  if (section) {
    changeSummary = `${section} Updated - ${entityName}`;
  } else if (fieldChanges.length === 1) {
    changeSummary = `${fieldChanges[0].fieldName} Updated - ${entityName}`;
  } else if (fieldChanges.length > 1) {
    changeSummary = `Multiple Fields Updated - ${entityName}`;
  } else {
    changeSummary = `${entityType} Updated - ${entityName}`;
  }

  // For location reassignments, update the summary
  if (changeType === 'ASSIGNMENT' && entityType === 'Location') {
    changeSummary = `Location Reassigned - ${entityName}`;
  }

  return {
    clientId,
    changeType: changeType || 'UPDATE',
    entityType: entityType || 'Location',
    entityId,
    entityName,
    changeSummary,
    changeReason: changeReason || 'Updated via Location Admin',
    changedBy: userId,
    changedByName: userName,
    fieldChanges,
    changedAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
    // Include affected entities if provided
    ...(affectedLocations && { affectedLocations }),
    ...(affectedOrgUnits && { affectedOrgUnits })
  };
};

/**
 * Format history entry for display
 * @param {object} entry - History entry from database
 * @returns {object} Formatted entry for UI display
 */
export const formatHistoryEntry = (entry) => {
  const changeDetails = entry.changeDetails || [];
  const firstDetail = changeDetails[0] || {};
  
  // Determine icon based on change type
  const getIcon = (changeType) => {
    const iconMap = {
      'CREATE': 'add_location',
      'UPDATE': 'edit',
      'DELETE': 'delete',
      'ASSIGNMENT': 'drive_file_move',
      'MANAGER_ASSIGNMENT': 'person_add',
      'STATUS_CHANGE': 'schedule',
      'LOCATION_UPDATE': 'edit',
      'ORGUNIT_UPDATE': 'account_tree'
    };
    return iconMap[changeType] || 'history';
  };

  // Format field changes for display
  const formatFieldChangesDisplay = (fieldChanges) => {
    if (!fieldChanges || fieldChanges.length === 0) return '';
    
    return fieldChanges.map(change => {
      return `${change.fieldName}: ${change.oldDisplayValue} → ${change.newDisplayValue}`;
    }).join('\n');
  };

  // Build description
  let description = '';
  if (firstDetail.fieldChanges && firstDetail.fieldChanges.length > 0) {
    description = formatFieldChangesDisplay(firstDetail.fieldChanges);
  } else if (entry.changeReason) {
    description = entry.changeReason;
  } else {
    description = entry.changeSummary || 'Changes made';
  }
  
  // Add affected entities info if available
  const affectedInfo = [];
  if (entry.affectedLocations && entry.affectedLocations.length > 0) {
    affectedInfo.push(`${entry.affectedLocations.length} location(s)`);
  }
  if (entry.affectedOrgUnits && entry.affectedOrgUnits.length > 0) {
    affectedInfo.push(`${entry.affectedOrgUnits.length} org unit(s)`);
  }

  return {
    id: entry.id,
    category: getCategory(entry.changeType),
    title: entry.changeSummary || `${firstDetail.entityType || 'Entity'} Updated`,
    description,
    icon: getIcon(entry.changeType),
    changedAt: entry.changedAt,
    changedBy: entry.changedBy,
    changedByName: entry.changedByName,
    changeReason: entry.changeReason,
    effectiveDate: entry.effectiveDate || entry.changedAt,
    isActive: !entry.endDate,
    entityType: firstDetail.entityType || entry.entityType,
    entityName: firstDetail.entityName || entry.entityName,
    affectedLocations: entry.affectedLocations,
    affectedOrgUnits: entry.affectedOrgUnits,
    affectedSummary: affectedInfo.length > 0 ? `Affected: ${affectedInfo.join(', ')}` : null,
    isIndirectChange: entry.isIndirectChange || false
  };
};

/**
 * Get category for change type
 * @param {string} changeType - Type of change
 * @returns {string} Category name
 */
const getCategory = (changeType) => {
  const categoryMap = {
    'ASSIGNMENT': 'Hierarchy Changes',
    'CREATION': 'Location Changes',
    'UPDATE': 'Location Changes',
    'LOCATION_UPDATE': 'Location Changes',
    'DELETION': 'Location Changes',
    'MANAGER_ASSIGNMENT': 'Management Changes',
    'LABOR_UPDATE': 'Labor & Targets',
    'ORGUNIT_UPDATE': 'Organizational Changes',
    'STATUS_CHANGE': 'Status Changes'
  };
  
  return categoryMap[changeType] || 'System Changes';
};

/**
 * Field mapping configurations for different sections
 */
export const FIELD_MAPPINGS = {
  basicInformation: {
    'locationName': { displayName: 'Location Name', type: 'text' },
    'locationNumber': { displayName: 'Location Number', type: 'text' },
    'address.street_1': { displayName: 'Street Address 1', type: 'text' },
    'address.street_2': { displayName: 'Street Address 2', type: 'text' },
    'address.city': { displayName: 'City', type: 'text' },
    'address.state': { displayName: 'State', type: 'text' },
    'address.zipCode': { displayName: 'ZIP Code', type: 'text' },
    'timeZone': { displayName: 'Time Zone', type: 'text' }
  },
  organizationalAssignment: {
    'orgLevel_1': { displayName: 'Level 1 Assignment', type: 'text' },
    'orgLevel_2': { displayName: 'Level 2 Assignment', type: 'text' },
    'orgLevel_3': { displayName: 'Level 3 Assignment', type: 'text' }
  },
  operationalStatus: {
    'openDate': { displayName: 'Open Date', type: 'date' },
    'closeDate': { displayName: 'Close Date', type: 'date' },
    'ownershipType': { displayName: 'Ownership Type', type: 'text' },
    'conceptId': { displayName: 'Concept', type: 'text' }
  },
  laborTargets: {
    'fohBaseHours': { displayName: 'FOH Base Hours', type: 'object' },
    'bohBaseHours': { displayName: 'BOH Base Hours', type: 'object' },
    'fohSplhTargets': { displayName: 'FOH SPLH Targets', type: 'object' },
    'bohSplhTargets': { displayName: 'BOH SPLH Targets', type: 'object' }
  }
};

export default {
  formatFieldValue,
  hasFieldChanged,
  createFieldChange,
  buildFieldChanges,
  createAuditLogData,
  formatHistoryEntry,
  FIELD_MAPPINGS
};